import * as React from 'react';
import './ProfessionalForm.scss';
import { TK } from '../../../store/translations/translationKeys';
import { Spinner, Form, ListGroup, ListGroupItem, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TextInputField, MultiplePhoneNumberField, AutocompleteTextField, SelectTextField } from '../fields';
import { IconButton } from '@mui/material';
import { ChromePicker } from 'react-color';
import { RequireProfessionalsManagement, RequirePricesManagement } from '../../RequirePermission';
import { FeatureEnum, ProfessionalData, ProfessionalPayment, ProfessionalPaymentType } from '../../../models';
import ChangeLogInfo from '../../ChangeLogInfo';
import { ChangeLog } from '../../../models';
import { ProfessionalEntity, UserEntity } from '../../../store/entities/state';
import { blockStyle, nameOnlyStyle } from '../../CustomDayCalendar/CustomDayCalendar';
import { useState, useMemo } from 'react';
import { DateIntervalField } from '../fields/DateIntervalField';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { hasFeature } from '../../../store/session/selectors';
import { AvailabilityField } from './AvailabilityField';
import { ProfessionalPaymentInput } from './ProfessionalPayment';
import { asSnake } from '../../../shared/string';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/DeleteRounded';
import Delete from '@material-ui/icons/Delete';
import { useGeneralSettings, useServicesSet } from '../../../store/settings/hooks';
import { useEntities } from '../../../store/entities/hooks';
import { getSelectedBranch } from '../../../store/general/selectors';
import { selectedBranchChange } from '../../../store/general/actions';
import { paths } from '../../../app/constants';
import styled from 'styled-components';
import { RequireAdministrator } from '../../RequirePermission';
import { entitiesLoaded, entityCreating } from '../../../store/entities/actions';
import ProfileAvatar from '../../ProfileAvatar/ProfileAvatar';
import { uploadMyProfessionalProfilePictureAsync, uploadProfessionalProfilePictureAsync, removeMyProfessionalProfilePictureAsync, removeProfessionalProfilePictureAsync, getAppointmentsAsync } from '../../../ajax/requests';
import { canEditProfessionals, getMyUser } from '../../../store/session/selectors';
import { getCompanyName } from '../../../store/settings/selectors';
import { updateSessionUser } from '../../../store/session/actions';
import { serviceId, serviceName } from '../../../shared/appointmint/appointment';
import { buildAutomaticUsername } from '../../../shared/username';
import AppButton from '../../AppButton';

const UserLinkageContainer = styled.div`
    padding: var(--spacer-S) 0;
    width: 100%;
    text-align: center;
    font-size: 0.75rem;
    line-height: 1.2;
    word-wrap: break-word;
    overflow-wrap: break-word;
`;

const UserText = styled.span`
    color: var(--text-muted);
    font-size: 0.75rem;
`;

const UserLink = styled.span<{ children?: React.ReactNode; onClick?: () => void }>`
    color: var(--appBlue);
    text-decoration: underline;
    cursor: pointer;
    font-weight: 500;

    &:hover {
        color: var(--appBlue);
        opacity: 0.8;
    }
`;

export interface ProfessionalFormStateProps {
    data: ProfessionalData,
    hasChanged: boolean,
    isSubmitting?: boolean,
    type: 'edit' | 'create',
    canEdit: boolean,
    changeLog?: ChangeLog[],
}

export interface ProfessionalFormDispatchProps {
    onChange: (data: ProfessionalEntity) => void,
    onLoad?: () => void,
    onSave: (data: ProfessionalData) => void,
    onCancel?: () => void,
    onDelete?: () => void,
}

export type ProfessionalFormProps = ProfessionalFormStateProps & ProfessionalFormDispatchProps;

const defaultPayment: ProfessionalPayment = {
    period: {
        from: '2000-01-01',
        to: '2999-12-31',
        description: '',
    },
    type: ProfessionalPaymentType.PerHour,
    value: 0,
}

const ProfessionalForm: React.FC<ProfessionalFormProps> = (props) => {
    const {
        type,
        data,
        hasChanged,
        isSubmitting,
        changeLog,
        canEdit,
        onChange,
        onLoad,
        onSave,
        onCancel,
        onDelete,
    } = props;

    const t = useTranslations();
    const dispatch = useDispatch();
    const history = useHistory();
    const generalSettings = useGeneralSettings();
    const selectedBranch = useSelector(getSelectedBranch);
    const capabilitiesSet = useServicesSet();
    const users = useEntities<UserEntity>('users');
    const [selectedService, setSelectedService] = useState<number|undefined>();
    const [showAddServiceModal, setShowAddServiceModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const hasBillingFeature = useSelector(hasFeature(FeatureEnum.Billing));
    const hasPrescriptionsFeature = useSelector(hasFeature(FeatureEnum.Prescriptions));
    const currentUser = useSelector(getMyUser);
    const canManageProfessionals = useSelector(canEditProfessionals);
    const companyName = useSelector(getCompanyName);

    const associatedUser = useMemo(() => {
        if (data.id) {
            return users.find(u => u.professionalId === data.id);
        }
        return null;
    }, [data.id, users]);

    // eslint-disable-next-line
    React.useEffect(() => { onLoad && onLoad(); }, []);

    const isEditing = type === 'edit';

    const handleNavigateToUser = React.useCallback(() => {
        if (associatedUser) {
            // Fill in missing user data from professional
            const updatedUser = {
                ...associatedUser,
                photoUrl: associatedUser.photoUrl || data.photoUrl || '',
                photoFileId: associatedUser.photoFileId || data.photoFileId,
                name: associatedUser.name || data.name || '',
                email: associatedUser.email || data.email || '',
            };

            // If we made changes, dispatch an update
            if (updatedUser.photoUrl !== associatedUser.photoUrl ||
                updatedUser.name !== associatedUser.name ||
                updatedUser.email !== associatedUser.email) {
                dispatch({ type: 'UPDATE_USER', payload: updatedUser });
            }

            history.push(`${paths.settingsUsers()}?id=${associatedUser.id}`);
        }
    }, [associatedUser, data.photoUrl, data.photoFileId, data.name, data.email, dispatch, history]);

    const handleCreateUser = React.useCallback(() => {
        // Pre-fill user data with professional information
        const initialUser: Partial<UserEntity> = {
            id: '',
            name: data.name,
            email: data.email,
            username: buildAutomaticUsername(data.email, data.name, companyName),
            photoUrl: data.photoUrl,
            photoFileId: data.photoFileId,
            professionalId: data.id,
            permissions: [],
        };

        // Dispatch entityCreating action with pre-filled data to open the user form modal
        dispatch(entityCreating<UserEntity>('users', initialUser as UserEntity));
    }, [data, dispatch]);

    const branches = generalSettings?.branches ?? {};
    const branchEntries = Object.entries(branches);

    const onBranchChange = React.useCallback((displayName: string) => {
        const branchId = branchEntries.find(([_, config]) => config.displayName === displayName)?.[0];
        if (branchId) dispatch(selectedBranchChange(branchId));
    }, [branchEntries, dispatch]);

    const isSelfProfessional = !!currentUser?.professionalId && currentUser.professionalId === data.id;

    const uploadHandler = React.useCallback((file: File) => {
        if (!data.id) {
            return Promise.reject(new Error('Professional id is required to upload an avatar'));
        }

        if (isSelfProfessional && !canManageProfessionals) {
            return uploadMyProfessionalProfilePictureAsync(file);
        }

        return uploadProfessionalProfilePictureAsync(data.id, file);
    }, [data.id, isSelfProfessional, canManageProfessionals]);

    const handleAvatarUpload = React.useCallback((url: string, photoFileId: string) => {
        const updatedProfessional = {
            ...data,
            photoUrl: url,
            photoFileId,
        };

        onChange(updatedProfessional);
        dispatch(entitiesLoaded('professionals', [updatedProfessional], [], 'AppendResult'));

        if (associatedUser) {
            const updatedUser = {
                ...associatedUser,
                photoUrl: url,
                photoFileId,
            };
            dispatch(entitiesLoaded('users', [updatedUser], [], 'AppendResult'));
        }

        if (isSelfProfessional) {
            dispatch(updateSessionUser({ photoUrl: url }));
        }
    }, [associatedUser, data, dispatch, isSelfProfessional, onChange]);

    const handleAvatarRemove = React.useCallback(async () => {
        if (!data.id) {
            return Promise.reject(new Error('Professional id is required to remove avatar'));
        }

        if (isSelfProfessional && !canManageProfessionals) {
            await removeMyProfessionalProfilePictureAsync();
        } else {
            await removeProfessionalProfilePictureAsync(data.id);
        }

        const updatedProfessional = {
            ...data,
            photoUrl: '',
            photoFileId: undefined,
        };

        onChange(updatedProfessional);
        dispatch(entitiesLoaded('professionals', [updatedProfessional], [], 'AppendResult'));

        if (associatedUser) {
            const updatedUser = {
                ...associatedUser,
                photoUrl: '',
                photoFileId: undefined,
            };
            dispatch(entitiesLoaded('users', [updatedUser], [], 'AppendResult'));
        }

        if (isSelfProfessional) {
            dispatch(updateSessionUser({ photoUrl: '' }));
        }
    }, [associatedUser, data, dispatch, isSelfProfessional, canManageProfessionals, onChange]);

    return (
        <>
            <Form className="profileForm">
                <section className="profileSection">
                    <div className="profileHeader">
                        <div className="avatarContainer">
                            <ProfileAvatar
                                src={data.photoUrl}
                                alt={data.name || ''}
                                displayName={data.name || ''}
                                email={data.email}
                                isEditable={isEditing}
                                onUploadSuccess={handleAvatarUpload}
                                onRemove={handleAvatarRemove}
                                uploadHandler={uploadHandler}
                                editLabel={t(TK.edit)}
                                removeLabel={t(TK.remove)}
                                onlyImageFilesAllowedMessage={t(TK.onlyImageFilesAllowed)}
                                fileSizeLimitMessage={t(TK.fileSizeMustBeLessThan2MB)}
                                profilePictureUpdatedMessage={t(TK.profilePictureUpdated)}
                                profilePictureRemovedMessage={t(TK.profilePictureRemoved)}
                                errorUploadingMessage={t(TK.errorUploadingProfilePicture)}
                            />
                            {isEditing && (
                                <RequireAdministrator>
                                    <UserLinkageContainer>
                                        {associatedUser ? (
                                            <UserText>
                                                {t(TK.user)}:{' '}
                                                <UserLink onClick={handleNavigateToUser}>
                                                    {associatedUser.username ?? associatedUser.name ?? associatedUser.email}
                                                </UserLink>
                                            </UserText>
                                        ) : (
                                            <UserLink onClick={handleCreateUser}>
                                                {t(TK.createUserForProfessional)}...
                                            </UserLink>
                                        )}
                                    </UserLinkageContainer>
                                </RequireAdministrator>
                            )}
                        </div>
                        <div className="profileFields">
                            <TextInputField
                                id={'professional-name'}
                                label={t(TK.name)}
                                value={data.name || ''}
                                onChange={name => onChange({...data, name})}
                                inputStyle={{ fontWeight: 'bold' }}
                            />

                            <TextInputField
                                id={'professional-email'}
                                label={t(TK.email)}
                                type="email"
                                value={data.email || ''}
                                onChange={email => onChange({...data, email})}
                            />

                            <MultiplePhoneNumberField
                                id={'professional-phone-numbers'}
                                placeholder={t(TK.phoneNumbers)}
                                values={data.phoneNumbers?.length ? data.phoneNumbers : ['']}
                                onChange={phoneNumbers => onChange({...data, phoneNumbers})}
                            />
                        </div>
                    </div>

                    <div className="biographyField">
                        <TextInputField
                            id={'professional-biography'}
                            label={t(TK.biography)}
                            helpText={t(TK.biographyPlaceholder)}
                            type='textarea'
                            value={data.biography || ''}
                            onChange={biography => onChange({...data, biography})}
                        />
                        <TextInputField
                            id={'professional-registration-number'}
                            label={t(TK.registrationNumber)}
                            value={data.registrationNumber || ''}
                            onChange={registrationNumber => onChange({...data, registrationNumber})}
                        />
                        {hasPrescriptionsFeature && (
                            <>
                                <SelectTextField
                                    id={'professional-ordem-profissional'}
                                    label={t(TK.prescriptionOrdemProfissional)}
                                    value={(() => {
                                        const codeToLabel: Record<string, string> = {
                                            '05': t(TK.prescriptionOrdemMedicos),
                                            '06': t(TK.prescriptionOrdemDentistas),
                                            '07': t(TK.prescriptionOrdemOdontologistas),
                                        };
                                        return codeToLabel[data.ordemProfissional ?? ''] ?? '';
                                    })()}
                                    options={[
                                        t(TK.prescriptionOrdemMedicos),
                                        t(TK.prescriptionOrdemDentistas),
                                        t(TK.prescriptionOrdemOdontologistas),
                                    ]}
                                    onValueChanged={label => {
                                        const labelToCode: Record<string, string> = {
                                            [t(TK.prescriptionOrdemMedicos)]: '05',
                                            [t(TK.prescriptionOrdemDentistas)]: '06',
                                            [t(TK.prescriptionOrdemOdontologistas)]: '07',
                                        };
                                        onChange({...data, ordemProfissional: labelToCode[label] ?? ''});
                                    }}
                                />
                                <TextInputField
                                    id={'professional-especialidade-codigo'}
                                    label={t(TK.prescriptionEspecialidadeCodigo)}
                                    value={data.pemEspecialidadeCodigo || ''}
                                    onChange={pemEspecialidadeCodigo => onChange({...data, pemEspecialidadeCodigo})}
                                />
                            </>
                        )}
                    </div>
                </section>

                {branchEntries.length > 1 && (
                    <section className="profileSection">
                        <div className="sectionHeader">
                            <h5 className="sectionTitle">{t(TK.branch)}</h5>
                        </div>
                        <AutocompleteTextField
                            id="availability-branch-selector"
                            value={branches[selectedBranch ?? '']?.displayName}
                            disableClearable
                            options={branchEntries.map(([_, config]) => config.displayName)}
                            onValueChanged={onBranchChange}
                        />
                    </section>
                )}

                <section className="profileSection">
                    <div className="sectionHeader">
                        <h5 className="sectionTitle">{t(TK.availability)}</h5>
                    </div>
                    <AvailabilityField
                        id={'professional-availability'}
                        values={selectedBranch ? data.branchTimeAvailability?.[selectedBranch] ?? [] : data.timeAvailability || []}
                        onChange={timeAvailability => onChange(selectedBranch
                            ? {...data, branchTimeAvailability: {...data.branchTimeAvailability, [selectedBranch]: timeAvailability}}
                            : {...data, timeAvailability})}
                    />
                </section>

                <section className="profileSection">
                    <div className="sectionHeader">
                        <h5 className="sectionTitle">{t(TK.availabilityExtra)}</h5>
                    </div>
                    <DateIntervalField
                        id={'professional-extra-availability'}
                        values={selectedBranch ? data.branchAvailabilityExtra?.[selectedBranch] ?? [] : data.availabilityExtra || []}
                        onChange={availabilityExtra => onChange(selectedBranch
                            ? {...data, branchAvailabilityExtra: {...data.branchAvailabilityExtra, [selectedBranch]: availabilityExtra}}
                            : {...data, availabilityExtra})}
                        enableRecurring={true}
                        addViaModal={true}
                        addModalTitle={t(TK.availabilityExtra)}
                    />
                </section>

                <section className="profileSection">
                    <div className="sectionHeader">
                        <h5 className="sectionTitle">{t(TK.absences)}</h5>
                    </div>
                    <DateIntervalField
                        id={'professional-absences'}
                        values={selectedBranch ? data.branchAbsences?.[selectedBranch] ?? [] : data.absences || []}
                        onChange={absences => onChange(selectedBranch
                            ? {...data, branchAbsences: {...data.branchAbsences, [selectedBranch]: absences}}
                            : {...data, absences})}
                        enableRecurring={true}
                        addViaModal={true}
                        addModalTitle={t(TK.absences)}
                        onValidateInterval={async interval => {
                            const appointments = await getAppointmentsAsync(interval.from.substring(0, 10), interval.to.substring(0, 10), undefined, true);
                            return appointments.filter(appt => {
                                if (data.id && appt.professionalId !== data.id) return false;
                                const apptStart = `${appt.date}T${appt.start}`;
                                const apptEnd = `${appt.date}T${appt.end}`;
                                return apptStart < interval.to && apptEnd > interval.from;
                            });
                        }}
                    />
                </section>

                <RequireProfessionalsManagement>
                    <>
                        <section className="profileSection">
                            <div className="sectionHeader">
                                <h5 className="sectionTitle">{t(TK.services)}</h5>
                            </div>
                            <ListGroup style={{marginBottom: 'var(--spacer-S)'}}>
                                {data.capabilities?.filter(c => !!c?.trim())?.map((c, idx) => (
                                    <ListGroupItem key={idx} style={{borderWidth: idx === selectedService ? '2px' : undefined}}>
                                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <AppButton color="link" onClick={() => setSelectedService(idx === selectedService ? undefined : idx)}>{serviceName(c, generalSettings)}</AppButton>
                                            <IconButton
                                                id={`remove-service-button-${asSnake(c)}`}
                                                onClick={() => {
                                                    const newCapabilities = data.capabilities?.filter((_, i) => i !== idx);
                                                    const newPaymentByCapability = {...data.paymentByCapability};
                                                    delete newPaymentByCapability[c];
                                                    onChange({
                                                        ...data,
                                                        capabilities: newCapabilities,
                                                        paymentByCapability: newPaymentByCapability,
                                                    });
                                                    if (idx === selectedService) {
                                                        setSelectedService(undefined);
                                                    }
                                                }}
                                                size="small"
                                                style={{color: 'red'}}
                                            >
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </div>
                                        { idx === selectedService && hasBillingFeature && !!data.capabilities?.length && <>
                                            <hr/>
                                            <RequirePricesManagement>
                                                <div style={{overflowX: 'auto'}}>
                                                    <ProfessionalPaymentInput
                                                        id={`professional-payment-${asSnake(c)}`}
                                                        key={c}
                                                        data={data.paymentByCapability?.[c] ?? [{...defaultPayment, ...data.paymentsByCapability?.[c]}]}
                                                        onChange={payments => onChange({
                                                            ...data,
                                                            paymentByCapability: {
                                                                ...data.paymentByCapability,
                                                                [c]: payments,
                                                            }
                                                        })}
                                                    />
                                                </div>
                                            </RequirePricesManagement>
                                        </>}
                                    </ListGroupItem>
                                ))}

                                <div style={{width: '100%', justifyContent: 'center', textAlign: 'center', marginTop: 'var(--spacer-S)'}}>
                                    <IconButton id={'add-service-button'} onClick={() => setShowAddServiceModal(true)} size="small">
                                        <AddCircleIcon />
                                    </IconButton>
                                    <span style={{verticalAlign: 'text-top', cursor: 'pointer'}} onClick={() => setShowAddServiceModal(true)}>{t(TK.add)}</span>
                                </div>
                            </ListGroup>
                        </section>

                        <section className="profileSection">
                            <div className="sectionHeader">
                                <h5 className="sectionTitle">{t(TK.color)}</h5>
                            </div>
                            <div>
                                <ChromePicker
                                    color={data.colorCode}
                                    onChange={color => onChange({...data, colorCode: color.hex})}
                                />
                                <div style={{marginTop: 'var(--spacer-S)'}}>
                                    <span style={{...blockStyle(data.colorCode), padding: '4px 8px'}}>{data.name}</span>
                                    <span style={{...nameOnlyStyle(data.colorCode), padding: '4px 8px'}}>{data.name}</span>
                                    <span style={{borderRadius: '100%', color: 'white', backgroundColor: data.colorCode, padding: '2px 4px'}}>25</span>
                                </div>
                            </div>
                        </section>
                    </>
                </RequireProfessionalsManagement>

                <div className="professionalForm__buttonsContainer">
                    <div className="buttonGroup">
                        { isEditing && canEdit && onDelete && (
                            <AppButton id={'professional-delete-button'} outline color="danger" onClick={() => setDeleteModal(true)}>
                                <Delete />
                            </AppButton>
                        )}
                        { onCancel && (<AppButton id={'professional-cancel-button'} onClick={onCancel}>{t(TK.cancel)}</AppButton>) }
                        <AppButton
                            id={'professional-save-button'}
                            color="success"
                            disabled={!hasChanged || isSubmitting}
                            onClick={() => onSave(data)}
                        >
                            {isSubmitting && <Spinner size="sm" color="secundary"/>}
                            {t(isEditing ? TK.saveChanges : TK.create)}
                        </AppButton>
                    </div>
                    { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
                </div>
            </Form>

            <Modal isOpen={showAddServiceModal} toggle={() => setShowAddServiceModal(false)}>
                <ModalHeader toggle={() => setShowAddServiceModal(false)}>{t(TK.addNewService)}</ModalHeader>
                <ModalBody>
                    <AutocompleteTextField
                        id={'professional-capabilities'}
                        values={(data.capabilities ?? []).map(s => serviceName(s, generalSettings))}
                        multiSelect={true}
                        options={capabilitiesSet.map(c => serviceName(c, generalSettings))}
                        onValuesChanged={values => onChange({
                            ...data,
                            capabilities: [...new Set(values.map(s => serviceId(s, generalSettings)))],
                        })}
                    />
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setShowAddServiceModal(false)}>
                        {t(TK.close)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={'professional-confirm-delete-button'} color="danger" onClick={() => { onDelete && onDelete(); setDeleteModal(false); }}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={'professional-abort-delete-button'} color="secondary" onClick={() => setDeleteModal(false)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>

        </>
    );
}

export default ProfessionalForm;
